import { useState, useEffect, useCallback } from 'react'
import { Send, AlertCircle, CheckCircle, RefreshCw, Clock, WifiOff, Search, Filter } from 'lucide-react'
import { fetchLembretesCobranca, fetchLembretesKPIs, reenviarLembrete } from '../../../src/services/api'
import type { LembreteCobrancaLog, LembretesKPIs } from '../../../types'
import { useAuth } from '../../../src/contexts/AuthContext'
import { useToast } from '../../../src/contexts/ToastContext'
import { ETAPAS_AUTOMATICAS, formatBRL, formatDia } from './etapas'

const PAGE_SIZE = 50

const STATUS_COLORS: Record<string, string> = {
  enviado:   'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  reenviado: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  falhou:    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  suprimido: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
}

/** 'suprimido' é o D+7 que não saiu porque alguém já falou com o cliente. */
const STATUS_LABEL: Record<string, string> = { suprimido: 'contato humano' }

interface KpiCardProps {
  icon: React.ReactNode
  label: string
  value: string | number
  bg: string
}

function KpiCard({ icon, label, value, bg }: KpiCardProps) {
  return (
    <div className={`${bg} rounded-lg p-4 flex items-center gap-3`}>
      <div className="flex-shrink-0">{icon}</div>
      <div>
        <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      </div>
    </div>
  )
}

/**
 * Log da régua automática (D-3, D0, D+3, D+7; D-1 na transição). Só leitura — o disparo é do cron
 * `lembrete-cobranca-diario` → `lembrete_fila` → `processar-fila-lembrete`.
 */
export default function AutomaticosTab() {
  const { user } = useAuth()
  const { addToast } = useToast()

  const [kpis, setKpis] = useState<LembretesKPIs | null>(null)
  const [lembretes, setLembretes] = useState<LembreteCobrancaLog[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [reenvioId, setReenvioId] = useState<string | null>(null)

  const [filters, setFilters] = useState({
    tipo: '',
    status: '',
    search: '',
    dataInicio: '',
    dataFim: '',
  })

  const filtrosApi = useCallback(() => ({
    tipo:       filters.tipo || undefined,
    status:     filters.status || undefined,
    search:     filters.search || undefined,
    dataInicio: filters.dataInicio || undefined,
    dataFim:    filters.dataFim || undefined,
  }), [filters])

  // Carrega a primeira página sempre que os filtros mudam.
  useEffect(() => {
    let cancelado = false
    setLoading(true)
    Promise.all([fetchLembretesKPIs(), fetchLembretesCobranca(filtrosApi(), 0, PAGE_SIZE)])
      .then(([kpisData, rows]) => {
        if (cancelado) return
        setKpis(kpisData)
        setLembretes(rows)
        setPage(0)
        setHasMore(rows.length === PAGE_SIZE)
      })
      .catch(() => { if (!cancelado) addToast('Erro ao carregar lembretes', 'error') })
      .finally(() => { if (!cancelado) setLoading(false) })
    return () => { cancelado = true }
  }, [filtrosApi]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Página seguinte. A versão anterior fazia `setPage(p => { loadData(); return p+1 })`
   * — o `loadData` lia o `page` velho do closure e recarregava sempre a mesma
   * página. Aqui a página vai como argumento.
   */
  async function carregarMais() {
    const proxima = page + 1
    setLoadingMore(true)
    try {
      const rows = await fetchLembretesCobranca(filtrosApi(), proxima, PAGE_SIZE)
      setLembretes(prev => [...prev, ...rows])
      setPage(proxima)
      setHasMore(rows.length === PAGE_SIZE)
    } catch {
      addToast('Erro ao carregar mais lembretes', 'error')
    } finally {
      setLoadingMore(false)
    }
  }

  async function handleReenviar(id: string) {
    setReenvioId(id)
    try {
      const userName = (user?.user_metadata as any)?.name ?? (user?.email ?? 'Usuário')
      await reenviarLembrete(id, userName)
      addToast('Lembrete recolocado na fila — sai em até 1 minuto', 'success')
      setLembretes(prev => prev.map(l => l.id === id
        ? { ...l, status: 'reenviado' as const, reenviado_por_nome: userName, reenviado_em: new Date().toISOString() }
        : l))
    } catch (e: any) {
      addToast(e?.message || 'Erro ao reenviar lembrete', 'error')
    } finally {
      setReenvioId(null)
    }
  }

  function clearFilters() {
    setFilters({ tipo: '', status: '', search: '', dataInicio: '', dataFim: '' })
  }

  const hasActiveFilters = filters.tipo || filters.status || filters.search || filters.dataInicio || filters.dataFim

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={<Send className="w-5 h-5 text-blue-500" />}
          label="Enviados hoje"
          value={kpis?.enviadosHoje ?? '—'}
          bg="bg-blue-50 dark:bg-blue-900/20"
        />
        <KpiCard
          icon={<CheckCircle className="w-5 h-5 text-emerald-500" />}
          label="Taxa de sucesso"
          value={kpis != null ? `${kpis.taxaSucesso}%` : '—'}
          bg="bg-emerald-50 dark:bg-emerald-900/20"
        />
        <KpiCard
          icon={<AlertCircle className="w-5 h-5 text-red-500" />}
          label="Falhas hoje"
          value={kpis?.falhasHoje ?? '—'}
          bg="bg-red-50 dark:bg-red-900/20"
        />
        <KpiCard
          icon={<WifiOff className="w-5 h-5 text-orange-500" />}
          label="Sem WhatsApp"
          value={kpis?.semWhatsapp ?? '—'}
          bg="bg-orange-50 dark:bg-orange-900/20"
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome ou UC..."
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={filters.tipo}
            onChange={e => setFilters(f => ({ ...f, tipo: e.target.value }))}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">Todos os tipos</option>
            {ETAPAS_AUTOMATICAS.map(e => (
              <option key={e.tipo} value={e.tipo}>{e.label} — {e.descricao}</option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">Todos os status</option>
            <option value="enviado">Enviado</option>
            <option value="reenviado">Reenviado</option>
            <option value="falhou">Falhou</option>
            <option value="suprimido">Não enviado (contato humano)</option>
          </select>

          <input
            type="date"
            value={filters.dataInicio}
            onChange={e => setFilters(f => ({ ...f, dataInicio: e.target.value }))}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <input
            type="date"
            value={filters.dataFim}
            onChange={e => setFilters(f => ({ ...f, dataFim: e.target.value }))}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />

          {hasActiveFilters ? (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-red-500 flex items-center gap-1"
            >
              <Filter className="w-4 h-4" />
              Limpar
            </button>
          ) : null}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">UC / Cliente</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Tipo</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Valor</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Vencimento</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">WhatsApp</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Status</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Enviado em</th>
                <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {lembretes.map(l => (
                <tr key={l.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-white">{l.uc_codigo}</div>
                    <div className="text-gray-500 dark:text-gray-400 text-xs">{l.nome_cliente ?? '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">
                      {l.tipo_lembrete}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-900 dark:text-white">
                    {l.valor_boleto != null ? formatBRL(l.valor_boleto) : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {formatDia(l.vencimento_boleto)}
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 font-mono text-xs">
                    {l.whatsapp_destino}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[l.status] ?? ''}`}>
                      {l.status === 'enviado'   ? <CheckCircle className="w-3 h-3" /> : null}
                      {l.status === 'reenviado' ? <RefreshCw   className="w-3 h-3" /> : null}
                      {l.status === 'falhou'    ? <AlertCircle className="w-3 h-3" /> : null}
                      {STATUS_LABEL[l.status] ?? l.status}
                    </span>
                    {l.error_details && l.status !== 'suprimido' ? (
                      <div className="text-xs text-red-500 mt-0.5 max-w-[200px] truncate" title={l.error_details}>
                        {l.error_details}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                    <div>{new Date(l.enviado_em).toLocaleDateString('pt-BR')}</div>
                    <div>{new Date(l.enviado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                  </td>
                  <td className="px-4 py-3">
                    {l.status === 'falhou' ? (
                      <button
                        onClick={() => handleReenviar(l.id)}
                        disabled={reenvioId === l.id}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors disabled:opacity-50"
                      >
                        <RefreshCw className={`w-3 h-3 ${reenvioId === l.id ? 'animate-spin' : ''}`} />
                        Reenviar
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
            Carregando...
          </div>
        ) : null}

        {!loading && lembretes.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Nenhum lembrete encontrado
          </div>
        ) : null}

        {hasMore && !loading ? (
          <div className="p-4 text-center border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={carregarMais}
              disabled={loadingMore}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
            >
              {loadingMore ? 'Carregando...' : 'Carregar mais'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
